/**
 * En engangslenke systemadminen gir videre for hånd (FF-11, #179, D7: ingen
 * e-post). Lenken vises bare her, én gang: serveren lagrer bare hashen, og
 * en ny lenke gjør denne ugyldig.
 */
import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { SystemFamilyLink } from '@stell/shared';
import { Button } from '../../components/primitives';

export function OneTimeLink({
  link,
  adminName,
  onDone,
}: {
  link: SystemFamilyLink;
  adminName: string;
  onDone: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const [copy, setCopy] = useState<'idle' | 'copied' | 'failed'>('idle');
  const url = new URL(link.url, window.location.origin).toString();

  async function copyUrl(): Promise<void> {
    try {
      await navigator.clipboard.writeText(url);
      setCopy('copied');
    } catch {
      setCopy('failed');
    }
  }

  return (
    <div
      data-testid="system-link"
      data-purpose={link.purpose}
      className="flex flex-col gap-3 rounded-md border border-card-line bg-sheet p-4"
    >
      <p className="m-0 text-sm font-extrabold text-ink">
        {t(link.purpose === 'enroll' ? 'system.link.enroll_title' : 'system.link.reset_title', { name: adminName })}
      </p>
      <p className="m-0 rounded-md bg-warn-bg px-3 py-2 text-sm font-bold text-ink">{t('system.link.once')}</p>
      <input
        readOnly
        value={url}
        aria-label={t('system.link.url')}
        onFocus={(e) => e.currentTarget.select()}
        data-testid="system-link-url"
        className="field text-sm"
      />
      <p className="m-0 text-xs text-ink-2">
        {t('system.link.expires', { at: new Date(link.expires_at).toLocaleString('nb-NO') })}
      </p>
      {copy === 'failed' ? (
        <p role="alert" className="m-0 text-sm text-bad">
          {t('system.link.copy_failed')}
        </p>
      ) : null}
      <div className="flex flex-wrap justify-end gap-3">
        <Button variant="secondary" onClick={() => void copyUrl()} data-testid="system-link-copy">
          <span aria-live="polite">{copy === 'copied' ? t('system.link.copied') : t('system.link.copy')}</span>
        </Button>
        <Button variant="ghost" onClick={onDone} data-testid="system-link-done">
          {t('system.link.done')}
        </Button>
      </div>
    </div>
  );
}
